import React from 'react';
import { AdapterProps } from '../types';
import InputLabel from '../../InputLabel/InputLabel';
import css from '../filters.module.scss';
import Button from '../../Button/Button';
import Icon from '../../Icon/Icon';
import Popup from '../../Popup/Popup';
import RelativeDatePicker from '../../RelativeDatePicker/RelativeDatePicker';
import { DateFormat } from '../../../index';
import { useTranslation } from 'utils/translation';
import { extractDays } from '../../RelativeDatePicker/useRelativePickerState';
import dayjs from 'dayjs';

type Value =
  | {
      from?: Date | undefined;
      to?: Date | undefined;
    }
  | string;

const getPickerKey = (value: Value | null | undefined) => {
  if (typeof value === 'string') {
    return value;
  }

  if (!value) {
    return 'empty';
  }

  const from = value.from ? dayjs(value.from).valueOf() : '';
  const to = value.to ? dayjs(value.to).valueOf() : '';

  return `${from}_${to}`;
};

const DateRangeAdapter = (props: AdapterProps<Value>) => {
  const { t } = useTranslation();
  const { label, onChange, onRemove, value } = props;
  const ref = React.useRef<{ close(): void | undefined }>();

  const valueAsString = () => {
    if (typeof value === 'string') {
      if (value.endsWith('_days')) {
        const mode = value.split('_')[0];
        return t(`filter.date_range.value_${mode}_days`, {
          days: extractDays(value),
        });
      }

      return t(`filter.date_range.value_${value}`);
    }

    if (value?.from || value?.to) {
      return (
        <>
          {value?.from && <DateFormat date={dayjs(value.from).toISOString()} />}
          {value?.to && (
            <>
              {' - '}
              <DateFormat date={dayjs(value.to).toISOString()} />
            </>
          )}
        </>
      );
    }
  };

  return (
    <div className={css.filter}>
      <InputLabel className={css.label}>{label}</InputLabel>
      <Popup
        actionsRef={ref}
        placement="top-start"
        button={<button className={css.button}>{valueAsString()}</button>}
      >
        <RelativeDatePicker
          key={getPickerKey(value)}
          defaultValue={value as any}
          onChange={(nextValue) => {
            onChange?.(nextValue);
            ref.current?.close();
          }}
        />
      </Popup>
      {onRemove && (
        <Button
          className={css['remove-button']}
          tabIndex={-1}
          onClick={onRemove}
          size="xs"
          variant="text"
          startIcon={<Icon name="fa fa-times" />}
        />
      )}
    </div>
  );
};

export default DateRangeAdapter;
